import { SUJETS_BOUSSOLE } from './sujets.js';
/** Le nom de l'assistante, au même endroit que sa base de connaissance. */
export const NOM_BOUSSOLE = 'Boussole';
/**
 * Réponse que personne n'attrape.
 *
 * Elle ne s'excuse pas et n'accuse pas la question : elle dit ce que Boussole sait faire.
 * Une aide qui répond « je n'ai pas compris » et s'arrête là laisse la personne exactement
 * où elle était.
 */
export const REPLI_BOUSSOLE = 'Je n’ai pas trouvé de réponse sûre à cette question. Je sais expliquer le calendrier et les échanges de jours, les dépenses et leur partage, la messagerie, les urgences, le journal, les fiches des enfants, l’export du dossier, l’abonnement et vos données. Une des propositions ci-dessous s’en approche peut-être.';
/**
 * Texte comparable : sans accent, sans majuscule, sans ponctuation.
 *
 * Un parent écrit « dépense » ou « depense », « où » ou « ou », avec ou sans apostrophe
 * typographique. Comparer les chaînes brutes ferait dépendre la reconnaissance d'un
 * clavier et d'une correction automatique.
 */
export function normaliser(texte) {
    const mots = texte
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .filter((mot) => mot !== '')
        .map(singulier);
    return ` ${mots.join(' ')} `;
}
/**
 * Pluriel français retiré, grossièrement et des deux côtés.
 *
 * « dépenses » et « dépense » doivent tomber sur le même sujet, et personne n'écrit sa
 * question au singulier parce qu'un mot-clé l'est. Le procédé abîme quelques mots au
 * passage — « mois » devient « moi » — sans conséquence : les mots-clés traversent
 * exactement la même fonction, donc les deux côtés s'abîment de la même façon. La borne à
 * quatre lettres protège les mots courts, où le « s » final est souvent la racine.
 */
function singulier(mot) {
    return mot.length >= 4 && /[sx]$/.test(mot) ? mot.slice(0, -1) : mot;
}
/**
 * Les mots d'un seul tenant qui ne désignent qu'un sujet.
 *
 * « solde », « journal », « urgence » : les rencontrer suffit à savoir de quoi on parle.
 * « parent », « dossier », « date » reviennent partout et ne désignent rien. La liste se
 * calcule à partir de la base plutôt que de s'écrire à la main — sans quoi ajouter un
 * sujet dévaluerait silencieusement les mots-clés d'un autre, et personne ne le verrait.
 */
const MOTS_DISCRIMINANTS = (() => {
    const occurrences = new Map();
    for (const sujet of SUJETS_BOUSSOLE) {
        for (const cle of new Set(sujet.motsCles.map((motCle) => normaliser(motCle).trim()))) {
            if (!cle.includes(' '))
                occurrences.set(cle, (occurrences.get(cle) ?? 0) + 1);
        }
    }
    return new Set([...occurrences].filter(([, compte]) => compte === 1).map(([cle]) => cle));
})();
/**
 * Ce que vaut un mot-clé reconnu.
 *
 * Le poids est un nombre de mots : une expression de plusieurs mots désigne un sujet,
 * un mot seul rarement. L'exception est le mot qui n'appartient qu'à ce sujet-là — il
 * porte autant qu'une expression, parce qu'il est tout aussi précis.
 */
function poids(cle) {
    const mots = cle.split(' ');
    return mots.length > 1 ? mots.length : MOTS_DISCRIMINANTS.has(cle) ? 2 : 1;
}
/**
 * À quel point ce sujet répond à cette question.
 *
 * Le score est un nombre de mots reconnus, pas une probabilité : il n'y a rien à
 * calibrer, et c'est voulu — la règle doit rester lisible par quelqu'un qui ajoute un
 * sujet six mois plus tard.
 *
 * Un mot-clé d'au moins cinq lettres reconnaît aussi les mots qui le prolongent :
 * « demande » attrape « demander », « conteste » attrape « contestée ». Sans cela, la
 * base devrait lister toutes les conjugaisons d'un verbe, ce que personne ne maintient.
 */
export function noterSujet(sujet, questionNormalisee) {
    let score = 0;
    for (const cle of new Set(sujet.motsCles.map((motCle) => normaliser(motCle).trim()))) {
        if (cle === '')
            continue;
        const reconnu = questionNormalisee.includes(` ${cle} `) ||
            (!cle.includes(' ') && cle.length >= 5 && new RegExp(` ${cle}[a-z]* `).test(questionNormalisee));
        if (reconnu)
            score += poids(cle);
    }
    return score;
}
/**
 * Sous ce seuil, on ne prétend pas avoir reconnu la question.
 *
 * À 1, un mot commun suffisait : « je ne trouve pas le code de la porte » tombait sur
 * l'invitation de l'autre parent, à cause du seul mot « code ». Une aide qui répond à
 * côté avec assurance coûte plus cher qu'une aide qui dit ne pas savoir.
 */
export const SEUIL_RECONNAISSANCE = 2;
/** Les sujets les plus proches, du meilleur au moins bon, seuil compris. */
export function sujetsProches(question, combien = 3) {
    const normalisee = normaliser(question);
    return SUJETS_BOUSSOLE.map((sujet) => ({ sujet, score: noterSujet(sujet, normalisee) }))
        .filter((candidat) => candidat.score >= SEUIL_RECONNAISSANCE)
        .sort((a, b) => b.score - a.score)
        .slice(0, combien)
        .map((candidat) => candidat.sujet);
}
/**
 * L'agent scripté : la réponse qui part quand l'IA ne peut pas, ou ne doit pas.
 *
 * Elle sort toujours quelque chose. C'est la propriété qui compte : le budget de jetons
 * peut être épuisé, la clé absente, Mistral en panne — un parent bloqué à 22 h doit
 * quand même lire une phrase utile.
 */
export function repondreScripte(question) {
    const proches = sujetsProches(question, 4);
    const sujet = proches[0] ?? null;
    return {
        reponse: sujet ? sujet.reponse : REPLI_BOUSSOLE,
        sujet,
        suggestions: sujet ? proches.slice(1, 3) : SUGGESTIONS_PAR_DEFAUT,
    };
}
/**
 * Ce qu'on propose à quelqu'un dont la question n'a rien accroché.
 *
 * Trois entrées, pas seize : une liste complète est un sommaire, et un sommaire n'aide
 * personne qui a déjà essayé de formuler sa question. Ce sont les trois écrans par
 * lesquels passent tous les dossiers.
 */
const SUGGESTIONS_PAR_DEFAUT = ['qui-a-enfant', 'declarer-depense', 'demander-echange']
    .map((id) => SUJETS_BOUSSOLE.find((sujet) => sujet.id === id))
    .filter((sujet) => sujet !== undefined);
//# sourceMappingURL=boussole.js.map